import { useState, useCallback } from 'react'

/**
 * Return type for useCollection hook
 */
export interface UseCollectionReturn<T> {
  /**
   * The current collection of items
   */
  items: T[]
  /**
   * Add a single item to the collection
   */
  addItem: (item: T) => void
  /**
   * Add multiple items to the collection
   */
  addItems: (items: T[]) => void
  /**
   * Remove an item from the collection
   */
  removeItem: (item: T) => void
  /**
   * Remove an item by index
   */
  removeItemAt: (index: number) => void
  /**
   * Update an item at a specific index
   */
  updateItemAt: (index: number, item: T) => void
  /**
   * Clear all items from the collection
   */
  clearItems: () => void
  /**
   * Set the entire collection (replace all items)
   */
  setItems: (items: T[]) => void
  /**
   * Check if the collection contains a specific item
   */
  hasItem: (item: T) => boolean
  /**
   * Get the count of items in the collection
   */
  count: number
  /**
   * Check if the collection is empty
   */
  isEmpty: boolean
}

/**
 * Custom hook for managing array collections
 *
 * Provides a consistent API for common array operations with proper
 * immutability and optimized re-renders.
 *
 * @param initialItems - Initial items for the collection (default: empty array)
 * @returns Object with collection state and management functions
 *
 * @example
 * ```tsx
 * const moves = useCollection<Move>([])
 *
 * // Add single item
 * moves.addItem(newMove)
 *
 * // Add multiple items
 * moves.addItems([move1, move2, move3])
 *
 * // Remove item
 * moves.removeItem(moveToRemove)
 *
 * // Clear all
 * moves.clearItems()
 *
 * // Check if empty
 * if (moves.isEmpty) {
 *   return <EmptyState />
 * }
 *
 * // Render items
 * return moves.items.map(move => <MoveCard key={move.id} move={move} />)
 * ```
 */
export function useCollection<T>(
  initialItems: T[] = []
): UseCollectionReturn<T> {
  const [items, setItems] = useState<T[]>(initialItems)

  const addItem = useCallback((item: T) => {
    setItems(prev => [...prev, item])
  }, [])

  const addItems = useCallback((newItems: T[]) => {
    setItems(prev => [...prev, ...newItems])
  }, [])

  const removeItem = useCallback((item: T) => {
    setItems(prev => {
      const index = prev.indexOf(item)
      if (index === -1) return prev
      return [...prev.slice(0, index), ...prev.slice(index + 1)]
    })
  }, [])

  const removeItemAt = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index))
  }, [])

  const updateItemAt = useCallback((index: number, item: T) => {
    setItems(prev => {
      const newItems = [...prev]
      newItems[index] = item
      return newItems
    })
  }, [])

  const clearItems = useCallback(() => {
    setItems([])
  }, [])

  const hasItem = useCallback(
    (item: T) => {
      return items.includes(item)
    },
    [items]
  )

  return {
    items,
    addItem,
    addItems,
    removeItem,
    removeItemAt,
    updateItemAt,
    clearItems,
    setItems,
    hasItem,
    count: items.length,
    isEmpty: items.length === 0,
  }
}
